import * as React from 'react'
import { useMemo } from 'react'

import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import MenuItem from '@mui/material/MenuItem'
import AlertTitle from '@mui/material/AlertTitle'
import Typography from '@mui/material/Typography'
import { Select } from '@workwave-tidal/core/components'
import { LightTooltip } from '@sales-center/components'
import FormHelperText from '@mui/material/FormHelperText'

import { useData, useHandlers } from './hooks'
import { ISalesFunnelStage } from '@user/store/salesFunnel'
import { IOpportunityIsCloseableResponse } from '@user/store/opportunity'

type Props = {
  disableOptions: boolean
  isCloseableDetails?: IOpportunityIsCloseableResponse
  name: string
  onClosedWonChange: (isClosedWon: boolean, isClosedLost: boolean) => void
  required?: boolean
  hideClosed?: boolean
}

export function OpportunityStageSelect(props: Props) {
  const {
    disableOptions,
    isCloseableDetails,
    name,
    onClosedWonChange,
    required,
    hideClosed = false,
  } = props
  const {
    getOptionLabel,
    options,
    initialOption,
    notCloseableMessage,
    isSubmitting,
    helperText,
    showError,
    isCloseWonSelected,
    height,
  } = useData({ isCloseableDetails, name, required })
  const { handelChange, handleBlur } = useHandlers({
    name,
    onClosedWonChange,
    required,
  })

  const stageOptions = useMemo(
    () => (hideClosed ? options.filter((option) => !option.closed) : options),

    [hideClosed, options],
  )

  return (
    <Box>
      <Select<ISalesFunnelStage>
        getLabel={getOptionLabel}
        required={required !== undefined ? required : true}
        disabled={isSubmitting}
        error={showError}
        items={stageOptions}
        onChange={handelChange}
        onBlur={handleBlur}
        value={initialOption}
        label={'Opportunity Stage'}
        testId="opportunity-stage-select"
        optionMode={'custom'}
        renderOption={(props, stage: ISalesFunnelStage) => {
          if (!isCloseableDetails?.closeable && stage.name == 'Closed/Won') {
            return (
              <LightTooltip
                key={stage.id}
                placement="top-start"
                title={notCloseableMessage}
              >
                <span>
                  <MenuItem
                    disabled
                    sx={{
                      height: height,
                      minHeight: height,
                    }}
                    {...props}
                    key={stage.id}
                    value={stage.id}
                  >
                    {stage.name}
                  </MenuItem>
                </span>
              </LightTooltip>
            )
          }
          return (
            <MenuItem
              sx={{
                height: height,
                minHeight: height,
              }}
              {...props}
              key={stage.id}
              value={stage.id}
              disabled={disableOptions}
            >
              {stage.name}
            </MenuItem>
          )
        }}
      />
      {showError && (
        <FormHelperText sx={{ ml: 1 }} error>
          {helperText}
        </FormHelperText>
      )}
      {isCloseWonSelected && (
        <Alert sx={{ mt: 1 }} severity="info">
          <AlertTitle sx={{ fontWeight: 600 }}>
            Opportunity Stage Closed Won
          </AlertTitle>
          <Typography variant="body2">
            This opportunity will be submitted to the closed/won queue upon
            saving.
          </Typography>
        </Alert>
      )}
    </Box>
  )
}
